/**
 * Extracts a human-readable error message from a Response object.
 *
 * Attempts to parse the response body as JSON and extract error details in this order:
 * 1. `errors[0].detail` (v2 API format)
 * 2. `detail` (simplified error format)
 * 3. `JSON.stringify(errorJson)` (fallback for unexpected formats)
 * 4. `res.statusText` (last resort)
 *
 * If JSON parsing fails, returns the response statusText.
 *
 * @param res - The Response object to extract error message from
 * @returns A Promise resolving to the extracted error message string
 *
 * @example
 * ```typescript
 * const res = await fetch('/api/resource');
 * if (!res.ok) {
 *   const errorMessage = await extractErrorMessageFromResponse(res);
 *   console.error(`Failed to fetch resource: ${errorMessage}`);
 * }
 * ```
 */
export async function extractErrorMessageFromResponse(
  res: Response,
): Promise<string> {
  let errorMessage = res.statusText;

  try {
    const errorJson = await res.json();
    errorMessage =
      // Primary format: v2 API errors array
      errorJson?.errors?.[0]?.detail ??
      // Simplified fallback for any other error format
      errorJson?.detail ??
      // Last-resort fallback for totally unexpected formats
      JSON.stringify(errorJson) ??
      errorMessage;
  } catch {
    /* ignore json parse errors - fallback to statusText */
  }

  return errorMessage;
}
